import type { MediaContract } from '@paperbits/common/media/mediaContract';
import type { LocalStyles } from '@paperbits/common/styles/localStyles';
import { BackgroundModel } from '@paperbits/common/widgets/background/backgroundModel';
import type { SiteKey } from '@tamtamy/paperbits-common/src/multisite/siteContract';
import { AltDefaultBackgrounds } from '../../altDefaultBackgrounds/altDefaultBackgrounds';
import type { ICmsContentPermalinkResolver } from '../../cms/content/ICmsContentPermalinkResolver';
import {
    type AltDefaultBackgroundCmsMetaKeyOptionValueOption,
    type CmsContentContract,
    cmsMetaExtractorAnyOption,
    cmsMetaExtractorAnyValue,
    cmsMetaExtractorValues,
    ContentTypeCmsMetaKeyOptionValueOption,
    ctaTypeCmsMetaKeyOptionValueValueOf,
    CtaTypeCmsMetaKeyOptionValueOption,
    type EventTypeCmsMetaKeyOptionValueOption,
    type OfficeTypeCmsMetaKeyOptionValueOption
} from '../../cms/content/ko/CmsContentContract';
import { EventTypes } from '../../eventTypes/eventTypes';
import type { DamMediaContract } from '../../media/damMediaContract';
import type { ICustomPermalinkResolver } from '../../media/ICustomPermalinkResolver';
import { AspectRatio } from '../../utils/imageTransform';
import { BundledSettingsProvider } from '../../configuration/bundledSettingsProvider';
import { HyperlinkModel } from '../../permalinks/hyperlinkModel';
import identifier from '../../utils/identifier';
import type { CategoryLabel } from '../../common/categories/categoryLabel';
import { CategoryLabels } from '../../common/categories/categoryLabels';
import { CardLayouts } from '../../common/cardLayouts/cardLayouts';
import { playerCustomizer } from '../dam-player/playerCustomizer';
import type { SiteContract } from '@tamtamy/paperbits-common/src/multisite/siteContract';

export interface ReplyCardModelInjectables {
    cmsContentPermalinkResolver: ICmsContentPermalinkResolver;
    mediaPermalinkResolver: ICustomPermalinkResolver;
}

function parseMetaMedia(value: string) {
    return typeof value === 'string' ? JSON.parse(value) : value;
}

export class ReplyCardModel {

    public cmsContentKey?: string;
    public cmsContentTitle?: string;
    public date?: number;
    public publicationDate?: number;
    public title: string;
    public abstract?: string;
    public categoryKey: string;
    public tags?: string[];
    public styles?: LocalStyles;
    public hyperlink?: HyperlinkModel;
    public layout: string;
    public applyOwnGrid: boolean;
    public applyDefaultBg: boolean;
    public logoSourceKey?: string;
    public logoMedia?: DamMediaContract;
    public logoIconKey?: string;
    public logoCaption?: string;
    public videoMedia?: DamMediaContract;
    public videoPlayer?: string;
    public videoPlayerHero?: string;
    public coverSourceKey?: string;
    public coverMedia?: DamMediaContract;
    public mediaBackground?: BackgroundModel;
    public ctaHyperlink?: HyperlinkModel;
    public ctaLabel?: string;
    public ctaType?: keyof typeof CtaTypeCmsMetaKeyOptionValueOption;
    public lowerDate?: number;
    public upperDate?: number;
    public timezone?: string;
    public eventType?: string;
    public location?: string;
    public altDefaultBackground?: string;
    public disableLazyLoadImage: boolean;
    public id: string;
    public downloadIcon?: boolean;

    // Offices
    public region?: string;
    public country?: string;
    public city?: string;
    public mainOffice?: boolean;
    public addressLine1?: string;
    public addressLine2?: string;
    public phone?: string;
    public fax?: string;
    public officeType: string;
    public googleMapsURL: string;

    // Company
    public infoLine?: string;
    public mainSites?: string [];
    public siteLinks?: string;

    public coverWidth?: number;

    constructor() {
        this.styles = {};
        this.applyOwnGrid = false;
        this.applyDefaultBg = false;
        this.disableLazyLoadImage = false;
        this.id = identifier();
    }

    public static async contentContractToModel(content: CmsContentContract, injected: ReplyCardModelInjectables, siteKey: SiteKey, omitContentKey: boolean = false, useDefaultLayout: boolean = false): Promise<ReplyCardModel> {
        const model = new ReplyCardModel();
        model.applyOwnGrid = false;
        model.id = content.id.toString();
        if (!omitContentKey) {
            model.cmsContentKey = content.id.toString();
            model.cmsContentTitle = content.title;
        }
        model.date = content.publicationDate && new Date(content.publicationDate).getTime();
        model.publicationDate = model.date;

        model.title = content.title;
        if (content.shortDescription?.trim().length) {
            model.abstract = content.shortDescription.trim();
        }
        if (content.title?.trim().length) {
            model.title = content.title.trim();
        }
        if (content.sectionPermalink) {
            model.categoryKey = content.sectionPermalink;
            const category: CategoryLabel[] = CategoryLabels.get(model.categoryKey);
            if (category.length) {
                for (const cat of category) {
                    if (!cat.publicationDate) {
                        delete model.date;
                    }
                }
            }
        } else {
            model.categoryKey = CategoryLabels.getEmptyKey();
        }
        
        if (content.eventDate) {
            const date = new Date(content.eventDate);
            if (!Number.isNaN(date.getTime())) {
                model.lowerDate = date.getTime();
            }
        }
        if (content.closingDate) {
            const date = new Date(content.closingDate);
            if (!Number.isNaN(date.getTime())) {
                model.upperDate = date.getTime();
            }
        }
        if (content.eventTimezone) {
            model.timezone = content.eventTimezone;
        }
        const hyperlink: HyperlinkModel = await injected.cmsContentPermalinkResolver.getHyperlinkByCmsContentContract(content, siteKey);
        if (hyperlink) {
            model.hyperlink = hyperlink;
        }

        // TODO Use anyOption after defining a specific interface
        const layout: string = cmsMetaExtractorAnyValue(content, 'card_type');
        const modelLayout = CardLayouts.getKeyByReferenceId(layout);

        if (modelLayout === 'card-wall-release-default') {
            const hyperlink = new HyperlinkModel();
            hyperlink.href = content.url;
            model.hyperlink = hyperlink
        }

        if (content.meta) {
            if (useDefaultLayout) {
                model.layout = CardLayouts.getDefaultKey();
            } else if (modelLayout) {
                model.layout = modelLayout;
            } else {
                model.layout = CardLayouts.get('card-wall-thumb-2-3-default')[0].key;
            }

            const cover: string = cmsMetaExtractorAnyValue(content, 'cover_media');
            if (cover) {
                let coverObj: MediaContract = null;
                try {
                    coverObj = parseMetaMedia(cover);
                } catch (error) {
                    console.error(`CMS content has inconsistent value for field "cover_media". The card may contain wrong data.`, error);
                }
                if (coverObj) {
                    model.coverMedia = coverObj;
                    // TODO: Media should handle different sources and resolutions according to viewport/device
                    let aspRatio = AspectRatio.OneByOne;
                    if (model.layout?.includes('event')) {
                        aspRatio = AspectRatio.SixteenByNine;
                    }
                    const url = await (injected.mediaPermalinkResolver as ICustomPermalinkResolver).getUrlFromMediaContract(coverObj, { aspectRatio: aspRatio });
                    if (url) {
                        const bgModel: BackgroundModel = new BackgroundModel();
                        bgModel.sourceUrl = `url("${url}")`;
                        model.mediaBackground = bgModel;
                    }
                }
            }
            const ctaURL: string = cmsMetaExtractorAnyValue(content, 'cta_url');
            if (ctaURL) {
                const ctaLabel: string = cmsMetaExtractorAnyValue(content, 'cta_label');
                const ctaType: CtaTypeCmsMetaKeyOptionValueOption = cmsMetaExtractorAnyOption<CtaTypeCmsMetaKeyOptionValueOption>(content, 'cta_type');
                if (ctaLabel?.length && ctaType?.length) {
                    const hyperlink: HyperlinkModel = new HyperlinkModel();
                    const label: string = ctaLabel;
                    hyperlink.href = ctaURL;
                    if (ctaType === 'mk_cta_type_link') {
                        hyperlink.target = '_blank';
                    }
                    hyperlink.title = label;
                    model.ctaHyperlink = hyperlink;
                    model.ctaLabel = label;
                    model.ctaType = ctaTypeCmsMetaKeyOptionValueValueOf(ctaType);
                }
            }
            const altBackground: AltDefaultBackgroundCmsMetaKeyOptionValueOption = cmsMetaExtractorAnyOption<AltDefaultBackgroundCmsMetaKeyOptionValueOption>(content, 'alt_background');
            if (altBackground) {
                model.altDefaultBackground = AltDefaultBackgrounds.getKeyByReferenceId(altBackground);
                if (model.altDefaultBackground) {
                    model.applyDefaultBg = true;
                }
            }
            const eventLocation = cmsMetaExtractorAnyValue(content, 'event_location');
            if (eventLocation) {
                model.location = eventLocation;
            }
            const eventType: EventTypeCmsMetaKeyOptionValueOption = cmsMetaExtractorAnyOption<EventTypeCmsMetaKeyOptionValueOption>(content, 'event_type');
            if (eventType) {
                model.eventType = EventTypes.getKeyByReferenceId(eventType);
            }
            const contentType: ContentTypeCmsMetaKeyOptionValueOption = cmsMetaExtractorAnyOption<ContentTypeCmsMetaKeyOptionValueOption>(content, 'content_type');
            if (
                contentType && contentType === ContentTypeCmsMetaKeyOptionValueOption.pdf
                && !model.ctaHyperlink && model.hyperlink
            ) {
                const hyperlink: HyperlinkModel = new HyperlinkModel();
                const label: string = 'Download';
                hyperlink.href = model.hyperlink.href;
                hyperlink.target = '_blank';
                hyperlink.title = label;
                hyperlink.query = model.hyperlink.query;
                model.ctaHyperlink = hyperlink;
                model.ctaLabel = label;
                model.ctaType = ctaTypeCmsMetaKeyOptionValueValueOf(CtaTypeCmsMetaKeyOptionValueOption.download);
                model.downloadIcon = true;
            }
            const logo: string = cmsMetaExtractorAnyValue(content, 'card_logo');
            if (logo) {
                try {
                    model.logoMedia = parseMetaMedia(logo);
                } catch (error) {
                    console.error(`CMS content has inconsistent value for field "card_logo". The card may contain wrong data.`, error);
                }
            }

            const video: string = cmsMetaExtractorAnyValue(content, 'card_video');
            if (video) {
                const {
                    autoplayPlayer,
                    heroPlayer,
                    standardPlayer,
                } = BundledSettingsProvider.newInstance().getSettingSync<{ autoplayPlayer: string, heroPlayer: string, standardPlayer: string }>('dam');
                try {
                    model.videoMedia = parseMetaMedia(video);
                    const ctx = {
                        site: {
                            key: siteKey,
                            language: siteKey.split('_')[1]
                        } as Partial<SiteContract>
                    }
                    const players = model.videoMedia.players;
                    model.videoPlayerHero = playerCustomizer(players?.[heroPlayer], undefined, ctx);
                    model.videoPlayer = playerCustomizer(players?.[standardPlayer] || players?.[autoplayPlayer], undefined, ctx);
                    // for hero cards with video opening CTA
                    const ctaLabel: string = cmsMetaExtractorAnyValue(content, 'cta_label');
                    const ctaType: CtaTypeCmsMetaKeyOptionValueOption = cmsMetaExtractorAnyOption<CtaTypeCmsMetaKeyOptionValueOption>(content, 'cta_type');
                    if (ctaLabel?.length && ctaType === 'mk_cta_type_button') {
                        model.ctaLabel = ctaLabel;
                        model.ctaType =  ctaTypeCmsMetaKeyOptionValueValueOf(ctaType);
                    }
                } catch (error) {
                    console.error(`CMS content has inconsistent value for field "card_video". The card may contain wrong data.`, error);
                }
            }

            const businessImpactTags = cmsMetaExtractorValues(content, 'business_impact', true);
            const industriesTags = cmsMetaExtractorValues(content, 'industries', true);

            const tags: string[] = [];
            if (industriesTags) {
                tags.push(...industriesTags.filter(t => t));
                model.tags = tags;
            }
            if (businessImpactTags) {
                tags.push(...businessImpactTags.filter(t => t));
                model.tags = tags;
            }
        }
        if (!model.mediaBackground && !model.altDefaultBackground) {
            model.applyDefaultBg = true;
            model.altDefaultBackground = AltDefaultBackgrounds.getDefaultKey();
        }

        const region: string = cmsMetaExtractorAnyValue(content, 'office_region');
        if (region) {
            model.region = region;
        }
        const country: string = cmsMetaExtractorAnyValue(content, 'office_country');
        if (country) {
            model.country = country;
        }
        const city: string = cmsMetaExtractorAnyValue(content, 'office_city');
        if (city) {
            model.city = city;
        }
        const mainOffice: string = cmsMetaExtractorAnyValue(content, 'office_main_office');
        if (mainOffice) {
            model.mainOffice = mainOffice === 'mk_office_main_office_yes' || mainOffice.toLowerCase() === 'yes';
        }
        const addressLine1: string = cmsMetaExtractorAnyValue(content, 'office_address_line_1');
        if (addressLine1) {
            model.addressLine1 = addressLine1;
        }
        const addressLine2: string = cmsMetaExtractorAnyValue(content, 'office_address_line_2');
        if (addressLine2) {
            model.addressLine2 = addressLine2;
        }
        const phone: string = cmsMetaExtractorAnyValue(content, 'office_phone');
        if (phone) {
            model.phone = phone;
        }
        const fax: string = cmsMetaExtractorAnyValue(content, 'office_fax');
        if (fax) {
            model.fax = fax;
        }
        const googleMapsURL: string = cmsMetaExtractorAnyValue(content, 'office_google_maps_url');
        if (googleMapsURL) {
            model.googleMapsURL = googleMapsURL;
        }
        const officeType: OfficeTypeCmsMetaKeyOptionValueOption = cmsMetaExtractorAnyOption<OfficeTypeCmsMetaKeyOptionValueOption>(content, 'office_office_type');
        if (officeType) {
            model.officeType = officeType;
        }
        const infoLine: string = cmsMetaExtractorAnyValue(content, 'website_info_label');
        if (infoLine) {
            model.infoLine = infoLine;
        }
        const siteLinks: string = cmsMetaExtractorAnyValue(content, 'website_site_links');
        if (siteLinks) {
            model.siteLinks = siteLinks;
        }
        const mainSites: string[] = cmsMetaExtractorValues(content, 'website_main_sites', true);
        if (mainSites) {
            model.mainSites = mainSites;
        }
        return model;
    }
}
